/**
 * CalendarService PRODUCTION - Sistema robusto de disponibilidad
 * Soluciona todos los problemas identificados:
 * 1. Sin horario de almuerzo hardcodeado
 * 2. Cache inteligente con invalidación automática  
 * 3. Disponibilidad en tiempo real
 * 4. Compatible con pacientes nuevos y existentes
 */

import { supabase } from '../lib/supabase/client';
import { getSantiagoOffset, toSantiagoISO } from '../lib/utils/dateUtils';

export interface TimeSlot {
  id: string;
  time: string;
  available: boolean;
  isPeak?: boolean;
  isLunch?: boolean;
  /** Bloqueado por la regla de negocio global (consultorio ocupado / traslado). */
  blockedGlobal?: boolean;
  /** Motivo del bloqueo global, para mostrar como tooltip o label. */
  blockedGlobalMotivo?: string;
}

export interface WorkingHours {
  start: number;
  end: number;
  slotDuration: number;
}

export class CalendarServiceProduction {
  private static readonly UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  static readonly WORKING_HOURS: WorkingHours = {
    start: 9,         // 9:00 AM (lunes a viernes)
    end: 20,          // Última cita a las 20:00
    slotDuration: 30  // 30 minutos por slot
  };

  // Horarios por día: 0=Dom, 1=Lun, ..., 5=Vie, 6=Sáb
  static readonly DAY_HOURS: Record<number, { start: number; end: number } | null> = {
    0: { start: 10, end: 14 }, // Domingo: 10:00–14:00
    1: { start: 9, end: 20 },  // Lunes
    2: { start: 9, end: 20 },  // Martes
    3: { start: 9, end: 20 },  // Miércoles
    4: { start: 9, end: 20 },  // Jueves
    5: { start: 9, end: 20 },  // Viernes
    6: { start: 9.5, end: 20 }, // Sábado: 09:30–20:00
  };

  // Cache global de disponibilidad
  private static availabilityCache: { [key: string]: { slots: TimeSlot[], timestamp: number } } = {};
  private static CACHE_DURATION = 5000; // ✅ Reducido a 5 segundos para mejor tiempo real

  // Cache de días completos bloqueados por el terapeuta (FUNC-111). Los bloqueos
  // de agenda cambian con baja frecuencia → TTL más largo que el de slots.
  private static blockedDatesCache: { [key: string]: { dates: Set<string>, timestamp: number } } = {};
  private static BLOCKED_CACHE_DURATION = 60_000; // 1 minuto

  /**
   * PRODUCTION: Generar slots respetando horarios por día de semana
   * Lun-Vie: 09:00–20:00 | Sáb: 09:30–20:00 | Dom: 10:00–14:00
   */
  static generateDailySlots(date: string): TimeSlot[] {
    console.log(`🏭 [PRODUCTION] Generando slots para ${date}`);

    const slots: TimeSlot[] = [];
    const { slotDuration } = this.WORKING_HOURS;

    const targetDate = new Date(date + 'T12:00:00');
    const dayOfWeek = targetDate.getDay();
    const dayHours = this.DAY_HOURS[dayOfWeek];

    if (!dayHours) {
      console.log(`🚫 [PRODUCTION] Día ${dayOfWeek} no tiene horario definido para ${date}`);
      return [];
    }

    const { start, end } = dayHours;
    const startHour = Math.floor(start);
    const startMinute = Math.round((start - startHour) * 60);
    const today = new Date();
    const isToday = targetDate.toDateString() === today.toDateString();

    for (let hour = startHour; hour <= end; hour++) {
      const firstMinute = (hour === startHour) ? startMinute : 0;
      for (let minute = firstMinute; minute < 60; minute += slotDuration) {
        if (hour === end && minute > 0) break;

        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

        let isPast = false;
        if (isToday) {
          const slotDateTime = new Date(date + `T${timeString}:00`);
          isPast = slotDateTime < today;
        }

        const isPeak = (hour >= 9 && hour <= 11) || (hour >= 15 && hour <= 17);

        slots.push({
          id: `${date}-${timeString}`,
          time: timeString,
          available: !isPast,
          isPeak,
          isLunch: false
        });
      }
    }

    const availableCount = slots.filter(s => s.available).length;
    const startLabel = `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
    console.log(`✅ [PRODUCTION] Generados ${slots.length} slots (${startLabel}–${end}:00), ${availableCount} disponibles`);

    return slots;
  }

  /**
   * Resolver el UUID real del terapeuta. `therapistId` puede llegar como slug
   * (booking público, viene de la URL / Professional.id) o ya como UUID.
   */
  private static async resolveTherapistUUID(therapistId: string): Promise<string | undefined> {
    if (this.UUID_REGEX.test(therapistId)) return therapistId;
    const { data } = await supabase
      .from('terapeutas')
      .select('id')
      .eq('slug', therapistId)
      .single();
    return data?.id;
  }

  /**
   * PRODUCTION (FUNC-111): fechas con bloqueo de DÍA COMPLETO del terapeuta
   * dentro de [startDate, endDate] (formato YYYY-MM-DD). Se usa para grisar días
   * enteros en la grilla del calendario — los bloqueos de franja horaria se
   * resuelven a nivel de slot vía `check_slot_availability`.
   *
   * Degrada a conjunto vacío ante cualquier error (RPC no desplegada, red, etc.):
   * el chequeo por slot y el guard server-side de `create_consultation` siguen
   * protegiendo — esto es solo la capa visual.
   */
  static async getBlockedDates(
    therapistId: string,
    startDate: string,
    endDate: string
  ): Promise<Set<string>> {
    const cacheKey = `${therapistId}-${startDate}-${endDate}`;
    const now = Date.now();
    const cached = this.blockedDatesCache[cacheKey];
    if (cached && now - cached.timestamp < this.BLOCKED_CACHE_DURATION) {
      return cached.dates;
    }

    try {
      const uuid = await this.resolveTherapistUUID(therapistId);
      if (!uuid) return new Set();

      const { data, error } = await supabase.rpc('get_blocked_dates_for_range', {
        p_therapist_id: uuid,
        p_start: startDate,
        p_end: endDate,
      });

      if (error) {
        // PGRST202 → RPC aún no desplegada; degradar silenciosamente.
        if ((error as { code?: string }).code !== 'PGRST202') {
          console.warn('⚠️ [PRODUCTION] get_blocked_dates_for_range error:', (error as { code?: string }).code);
        }
        return new Set();
      }

      const rows = (data ?? []) as Array<{ fecha: string }>;
      const dates = new Set<string>(rows.map(r => r.fecha));
      this.blockedDatesCache[cacheKey] = { dates, timestamp: now };
      return dates;
    } catch (err) {
      console.error('❌ [PRODUCTION] getBlockedDates:', err);
      return new Set();
    }
  }

  /**
   * PRODUCTION: Verificar disponibilidad real con cache inteligente
   * duracionMinutos: duración real del nuevo booking (50, 90, etc.) para detectar
   * solapamientos correctamente. Usar WORKING_HOURS.slotDuration (30 min) subestima
   * los conflictos cuando la sesión dura más que el ancho de la celda.
   */
  static async checkRealAvailability(
    therapistId: string,
    date: string,
    baseSlots: TimeSlot[],
    modality?: 'presencial' | 'online' | 'ambas' | 'domicilio',
    duracionMinutos: number = this.WORKING_HOURS.slotDuration
  ): Promise<TimeSlot[]> {
    console.log(`🔍 [PRODUCTION] Verificando disponibilidad real para terapeuta ${therapistId} el ${date}`);

    try {
      // Resolver UUID real del terapeuta. therapistId puede llegar como slug
      // (booking público, viene de la URL) o ya como UUID (portal, viene de
      // terapeutas.id) — buscar por slug cuando ya es un UUID no matchea nada
      // y el fallback silencioso devuelve baseSlots (todo "disponible").
      let realTherapistId: string;
      if (this.UUID_REGEX.test(therapistId)) {
        realTherapistId = therapistId;
      } else {
        const { data: therapist, error: therapistError } = await supabase
          .from('terapeutas')
          .select('id')
          .eq('slug', therapistId)
          .single();

        if (therapistError || !therapist) {
          console.warn(`⚠️ [PRODUCTION] No se encontró terapeuta con slug: ${therapistId}, usando slots base`);
          return baseSlots;
        }

        realTherapistId = therapist.id;
      }

      // FIX PRODUCCIÓN: Usar RPC con SECURITY DEFINER para evitar bloqueo RLS en rol anon.
      // El SELECT directo a 'consultas' devuelve vacío en producción para usuarios no autenticados,
      // haciendo que todos los slots aparezcan disponibles (el bug reportado en 4e).
      // La RPC get_booked_slots_for_date fue creada en sql/fix_fase2_2026_04.sql.
      const { data: therapistBookings, error: consultasError } = await supabase
        .rpc('get_booked_slots_for_date', {
          p_therapist_id: realTherapistId,
          p_date: date
        });

      if (consultasError) {
        console.warn('⚠️ [PRODUCTION] Error en RPC get_booked_slots_for_date:', consultasError);
        return baseSlots;
      }

      console.log(`👨‍⚕️ [PRODUCTION] ${therapistBookings?.length || 0} citas bloqueadas para ${date}`);

      // FIX TIMEZONE: offset dinámico America/Santiago — no hardcodear -03:00
      const offset = getSantiagoOffset(date);

      // Marcar slots ocupados comparando con citas reales de la BD
      const updatedSlots = baseSlots.map(slot => {
        if (!slot.available) return slot;

        const slotDateTime = new Date(`${date}T${slot.time}:00${offset}`);

        const hasConflict = (therapistBookings || []).some((booking: { fecha_hora: string; duracion: number }) => {
          const bookingStart = new Date(booking.fecha_hora);
          const bookingEnd   = new Date(bookingStart.getTime() + booking.duracion * 60_000);
          const slotEnd      = new Date(slotDateTime.getTime() + duracionMinutos * 60_000);
          return slotDateTime < bookingEnd && slotEnd > bookingStart;
        });

        return { ...slot, available: !hasConflict };
      });

      // Si es presencial, verificar disponibilidad del consultorio físico
      if (modality === 'presencial') {
        const { data: clinicBlocked, error: clinicError } = await supabase
          .rpc('get_clinic_blocked_slots_for_date', {
            p_date: date,
            p_slot_duration: this.WORKING_HOURS.slotDuration
          });

        if (!clinicError && clinicBlocked) {
          const slotsWithClinic = updatedSlots.map(slot => {
            if (!slot.available) return slot;

            const slotDateTime = new Date(`${date}T${slot.time}:00${offset}`);

            const clinicConflict = (clinicBlocked as { slot_start: string }[]).some((blocked) => {
              const blockedStart = new Date(blocked.slot_start);
              return Math.abs(slotDateTime.getTime() - blockedStart.getTime()) < 60_000;
            });

            return { ...slot, available: !clinicConflict };
          });

          const available = slotsWithClinic.filter(s => s.available).length;
          console.log(`✅ [PRODUCTION] ${available} horarios disponibles (presencial con consultorio)`);
          return slotsWithClinic;
        }
      }

      const available = updatedSlots.filter(s => s.available).length;
      console.log(`✅ [PRODUCTION] ${available} horarios disponibles`);
      return updatedSlots;

    } catch (error) {
      console.error('❌ [PRODUCTION] Error en checkRealAvailability:', error);
      return baseSlots;
    }
  }

  /**
   * PRODUCTION: Función principal con cache inteligente
   */
  static async getAvailableSlots(
    therapistId: string,
    date: string,
    forceRefresh: boolean = false,
    modality?: 'presencial' | 'online' | 'ambas' | 'domicilio',
    duracion: number = 50
  ): Promise<TimeSlot[]> {
    const cacheKey = `${therapistId}-${date}-${modality ?? 'any'}-${duracion}`;
    const now = Date.now();

    // Verificar cache (solo si no es refresh forzado)
    if (!forceRefresh && this.availabilityCache[cacheKey]) {
      const cached = this.availabilityCache[cacheKey];
      if (now - cached.timestamp < this.CACHE_DURATION) {
        console.log(`📋 [PRODUCTION] Usando cache para ${therapistId} el ${date}`);
        return cached.slots;
      }
    }

    console.log(`🔍 [PRODUCTION] Obteniendo slots frescos para ${therapistId} el ${date}`);

    try {
      // 1. Generar slots base (sin restricciones artificiales)
      const baseSlots = this.generateDailySlots(date);

      // 1b. Día completo bloqueado por el terapeuta (FUNC-111). Corta acá,
      //     sin importar la modalidad — el bloqueo de agenda es transversal.
      const blockedDates = await this.getBlockedDates(therapistId, date, date);
      if (blockedDates.has(date)) {
        const blocked = baseSlots.map(s => ({
          ...s,
          available: false,
          blockedGlobal: true,
          blockedGlobalMotivo: 'El terapeuta no atiende este día',
        }));
        this.availabilityCache[cacheKey] = { slots: blocked, timestamp: now };
        console.log(`🚫 [PRODUCTION] ${date} es día bloqueado por el terapeuta — 0 slots disponibles`);
        return blocked;
      }

      // 2. Verificar disponibilidad real en BD (consultas del terapeuta + consultorio)
      const realSlots = await this.checkRealAvailability(therapistId, date, baseSlots, modality, duracion);

      // 3. Verificar bloqueos de regla de negocio global (check_slot_availability):
      //    consultorio lleno, transición presencial→online y bloqueos de agenda
      //    de FRANJA HORARIA del terapeuta (CHECK 0 de esa RPC). Se corre para
      //    toda modalidad concreta que la RPC acepta ('presencial'|'online'|
      //    'domicilio') — antes 'domicilio' se saltaba el chequeo y un bloqueo
      //    parcial no se reflejaba. Si la RPC no existe aún, checkGlobalBlocks
      //    lo maneja graciosamente.
      let finalSlots = realSlots;
      if (modality === 'presencial' || modality === 'online' || modality === 'domicilio') {
        // Resolver UUID del terapeuta (puede ser slug o ya un UUID — ver nota en checkRealAvailability)
        let therapistUUID: string | undefined = this.UUID_REGEX.test(therapistId) ? therapistId : undefined;
        if (!therapistUUID) {
          const { data: therapist } = await supabase
            .from('terapeutas')
            .select('id')
            .eq('slug', therapistId)
            .single();
          therapistUUID = therapist?.id;
        }

        if (therapistUUID) {
          finalSlots = await this.checkGlobalBlocks(
            therapistUUID,
            date,
            realSlots,
            modality,
            duracion
          );
          const globalBlocked = finalSlots.filter(s => s.blockedGlobal).length;
          if (globalBlocked > 0) {
            console.log(`🚫 [PRODUCTION] ${globalBlocked} slots bloqueados por regla de negocio global`);
          }
        }
      }

      // 4. Guardar en cache
      this.availabilityCache[cacheKey] = {
        slots: finalSlots,
        timestamp: now
      };

      const availableCount = finalSlots.filter(s => s.available).length;
      console.log(`✅ [PRODUCTION] Retornando ${availableCount} slots disponibles`);

      return finalSlots;

    } catch (error) {
      console.error('💥 [PRODUCTION] Error crítico en getAvailableSlots:', error);
      return this.createEmergencySlots(date);
    }
  }

  /**
   * PRODUCTION: Limpiar cache globalmente
   */
  static clearCache(): void {
    console.log('🧹 [PRODUCTION] Limpiando cache de disponibilidad');
    this.availabilityCache = {};
  }

  /**
   * PRODUCTION: Limpiar cache para fecha específica.
   * Elimina todas las entradas que correspondan a therapistId+date,
   * independientemente de la modalidad o duración usada al cachear.
   */
  static clearCacheForDate(therapistId: string, date: string): void {
    const prefix = `${therapistId}-${date}-`;
    let deleted = 0;
    Object.keys(this.availabilityCache).forEach(key => {
      if (key.startsWith(prefix)) {
        delete this.availabilityCache[key];
        deleted++;
      }
    });
    console.log(`🧹 [PRODUCTION] Cache limpiado para ${therapistId} el ${date} (${deleted} entradas)`);
  }

  /**
   * Slots de emergencia
   */
  static createEmergencySlots(date: string): TimeSlot[] {
    console.log('🆘 [PRODUCTION] Creando slots de emergencia');
    
    const today = new Date();
    const targetDate = new Date(date + 'T00:00:00');
    const isToday = targetDate.toDateString() === today.toDateString();

    const emergencyTimes = [
      { hour: 10, minute: 0 }, { hour: 10, minute: 30 },
      { hour: 11, minute: 0 }, { hour: 11, minute: 30 },
      { hour: 15, minute: 0 }, { hour: 15, minute: 30 },
      { hour: 16, minute: 0 }, { hour: 16, minute: 30 },
      { hour: 17, minute: 0 }, { hour: 18, minute: 0 }
    ];

    return emergencyTimes.map(({ hour, minute }) => {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      
      let isPast = false;
      if (isToday) {
        const slotDateTime = new Date(date + `T${timeString}:00`);
        isPast = slotDateTime < today;
      }

      return {
        id: `emergency-${date}-${timeString}`,
        time: timeString,
        available: !isPast,
        isPeak: hour >= 15 && hour <= 17,
        isLunch: false
      };
    });
  }

  /**
   * Validar disponibilidad específica
   */
  static async validateSlotAvailability(
    therapistId: string,
    date: string,
    time: string,
    modality?: 'presencial' | 'online' | 'ambas' | 'domicilio'
  ): Promise<boolean> {
    try {
      const slots = await this.getAvailableSlots(therapistId, date, true, modality);
      const targetSlot = slots.find(slot => slot.time === time);
      const isAvailable = targetSlot?.available || false;
      
      console.log(`🔒 [PRODUCTION] Slot ${date} ${time} para ${therapistId}: ${isAvailable ? 'DISPONIBLE' : 'OCUPADO'}`);
      return isAvailable;
    } catch (error) {
      console.error('❌ [PRODUCTION] Error validando slot:', error);
      return false;
    }
  }

  /**
   * Verificar bloqueos de negocio global (consultorio / traslado) para un conjunto
   * de slots usando la RPC check_slot_availability.
   *
   * Solo se aplica a slots que ya están marcados como `available: true` — los slots
   * ya ocupados por consulta del terapeuta no necesitan este check.
   *
   * Los checks se hacen en paralelo (Promise.all) en lotes de 10 para evitar
   * waterfalls sin saturar la conexión.
   *
   * Si la RPC no existe en la BD (migración aún no aplicada), los slots se
   * retornan sin modificación — el error se detecta por el código PGRST202.
   */
  static async checkGlobalBlocks(
    therapistUUID: string,
    date: string,
    slots: TimeSlot[],
    modality: 'presencial' | 'online' | 'domicilio',
    duracionMinutos: number = 50
  ): Promise<TimeSlot[]> {
    const availableSlots = slots.filter(s => s.available);

    if (availableSlots.length === 0) return slots;

    const BATCH_SIZE = 10;
    const results: Map<string, { disponible: boolean; motivo: string | null }> = new Map();

    for (let i = 0; i < availableSlots.length; i += BATCH_SIZE) {
      const batch = availableSlots.slice(i, i + BATCH_SIZE);

      const checks = batch.map(slot => {
        const fechaHoraISO = toSantiagoISO(date, slot.time);
        return supabase
          .rpc('check_slot_availability', {
            p_fecha_hora: fechaHoraISO,
            p_duracion: duracionMinutos,
            p_terapeuta_id: therapistUUID,
            p_modalidad: modality
          })
          .then(({ data, error }) => {
            if (error) {
              // PGRST202 → función no existe aún en BD; ignorar silenciosamente
              if ((error as any).code !== 'PGRST202') {
                console.error('[checkGlobalBlocks] RPC error:', (error as any).code);
              }
              return { slotId: slot.id, disponible: true, motivo: null };
            }
            const payload = data as { disponible: boolean; motivo: string | null } | null;
            return {
              slotId: slot.id,
              disponible: payload?.disponible ?? true,
              motivo: payload?.motivo ?? null
            };
          });
      });

      const batchResults = await Promise.all(checks);
      batchResults.forEach(r => results.set(r.slotId, { disponible: r.disponible, motivo: r.motivo }));
    }

    return slots.map(slot => {
      const result = results.get(slot.id);
      if (!result || result.disponible) return slot;

      return {
        ...slot,
        available: false,
        blockedGlobal: true,
        blockedGlobalMotivo: result.motivo ?? undefined
      };
    });
  }

  /**
   * Formatear hora para display — 24h con dos puntos y sufijo am/pm pegado (ej. "17:30pm")
   */
  static formatTimeForDisplay(time: string): string {
    const [hour, minute] = time.split(':');
    const hourNum = parseInt(hour);
    const period = hourNum >= 12 ? 'pm' : 'am';

    return `${hour.padStart(2, '0')}:${minute}${period}`;
  }

  /**
   * Verificar si es día con horario disponible (incluye fines de semana)
   */
  static isWorkingDay(date: string): boolean {
    const dayOfWeek = new Date(date + 'T12:00:00').getDay();
    return this.DAY_HOURS[dayOfWeek] !== null && this.DAY_HOURS[dayOfWeek] !== undefined;
  }
}

// Exponer función global para limpiar cache
(window as any).clearCalendarCache = () => {
  CalendarServiceProduction.clearCache();
  console.log('🧹 Cache de calendario limpiado globalmente');
};

// ✅ NOTIFICACIÓN GLOBAL: Limpiar cache cuando se complete un booking desde cualquier lugar
(window as any).notifyBookingCompleted = (therapistId: string, date: string) => {
  console.log(`📢 [GLOBAL] Booking completado - limpiando cache para ${therapistId} el ${date}`);
  CalendarServiceProduction.clearCacheForDate(therapistId, date);
  CalendarServiceProduction.clearCache(); // También limpiar cache global
  
  // Disparar evento personalizado para que los calendarios activos se actualicen
  window.dispatchEvent(new CustomEvent('bookingCompleted', {
    detail: { therapistId, date }
  }));
};

// Funciones de debug — solo en desarrollo, nunca en el bundle de producción
// (evita exponer PII de terapeutas/consultas por consola — regla CLAUDE.md)
if (process.env.NODE_ENV === 'development') {
  (window as any).debugCalendarProduction = async () => {
    console.log('🧪 [PRODUCTION] TESTING CALENDAR SERVICE...');

    const testDate = '2026-03-30';
    const testTherapist = 'angel-diaz';

    try {
      const slots = await CalendarServiceProduction.getAvailableSlots(testTherapist, testDate);
      console.log('✅ [PRODUCTION] TEST EXITOSO - Slots obtenidos:', slots.length);
      console.log('📋 [PRODUCTION] Slots disponibles:', slots.filter(s => s.available).length);
      console.log('🔍 [PRODUCTION] Primeros 5 slots:', slots.slice(0, 5));
    } catch (error) {
      console.error('❌ [PRODUCTION] TEST FALLÓ:', error);
    }
  };

  (window as any).testDatabaseQuery = async () => {
    console.log('🔍 [DB-TEST] Probando query de consultas directamente...');

    try {
      const { supabase } = await import('../lib/supabase/client');

      // Test 1: Ver todas las consultas programadas
      const { data: allConsultas } = await supabase
        .from('consultas')
        .select('*')
        .in('estado', ['programada', 'confirmada', 'agendada'])
        .order('fecha_hora', { ascending: true });

      console.log('📊 [DB-TEST] Total consultas programadas:', allConsultas?.length || 0);

      if (allConsultas && allConsultas.length > 0) {
        console.log('🔍 [DB-TEST] Primeras 3 consultas:', allConsultas.slice(0, 3).map(c => ({
          id: c.id,
          fecha_hora: c.fecha_hora,
          terapeuta_id: c.terapeuta_id,
          estado: c.estado,
          duracion: c.duracion
        })));
      }

      // Test 2: Ver terapeutas disponibles (sin nombres — PII)
      const { data: terapeutas } = await supabase
        .from('terapeutas')
        .select('id, slug, activo')
        .eq('activo', true);

      console.log('👨‍⚕️ [DB-TEST] Terapeutas activos:', terapeutas?.length || 0);

      return { consultas: allConsultas, terapeutas };
    } catch (error) {
      console.error('❌ [DB-TEST] Error:', error);
    }
  };
}

export { CalendarServiceProduction as CalendarService };
export default CalendarServiceProduction;